import { useQuery } from '@apollo/client';
import { Add } from '@mui/icons-material';
import { Autocomplete, Button, Paper, TextField, type PaperProps } from '@mui/material';
import deepEqual from 'fast-deep-equal';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { namedByPlaceholder } from '~/components/forms/autocomplete-label';
import { useSettingIds } from '~/components/settings';
import { useAppContext } from '~/contexts';
import type { DeviceDynamicCollectionSource, DeviceSignalTarget } from '~/generated/graphql';
import { AutocompleteGroupHeader, AutocompleteGroupItems } from '..';
import { NewDeviceCollectionDialog } from './NewDeviceCollectionDialog';
import type {
  AvailableTargets_DeviceDynamicCollection,
  AvailableTargets_DeviceGroupNew,
} from './TargetAutocomplete.generated';
import { AvailableTargetsDocument } from './TargetAutocomplete.generated';

const indexOptionDataById = (
  items: readonly AvailableTargets_DeviceGroupNew[] | undefined,
): Record<number, { name: string; deviceCount: number } | undefined> =>
  Object.fromEntries(
    items?.map(({ id, name, deviceCount }) => [id, { name, deviceCount }] as const) ?? [],
  );

const indexDynamicCollectionDataById = (
  items: readonly AvailableTargets_DeviceDynamicCollection[],
): Record<
  number,
  { name: string; deviceCount: number; source: DeviceDynamicCollectionSource } | undefined
> =>
  Object.fromEntries(
    items.map(({ id, name, deviceCount, source }) => [id, { name, deviceCount, source }] as const),
  );

export interface TargetAutocompleteProps {
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  name?: string;
  onBlur?: React.FocusEventHandler;
  onChange?: (value: DeviceSignalTarget | null) => void;
  value?: DeviceSignalTarget | null;
}

export const TargetAutocomplete = ({
  disabled,
  error,
  helperText,
  name,
  onBlur,
  onChange,
  value,
}: TargetAutocompleteProps) => {
  const { t } = useTranslation();
  // Inside a settings row the row's label names the input.
  const ids = useSettingIds();
  const { currentNetwork } = useAppContext();
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data, loading, refetch } = useQuery(AvailableTargetsDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const dynamicCollections = useMemo(
    () => data?.network?.deviceDynamicCollections ?? [],
    [data?.network?.deviceDynamicCollections],
  );

  // MUI Autocomplete groups consecutive items, so collections must be partitioned
  // by source (MANUAL before SAVED_FILTER) to avoid duplicate group headers.
  const options = useMemo(
    (): DeviceSignalTarget[] => [
      { allDevices: true },
      ...(data?.network?.allDeviceGroups.map(({ id }) => ({ deviceGroupId: id })) ?? []),
      ...dynamicCollections
        .filter(({ source }) => source === 'MANUAL')
        .map(({ id }) => ({ deviceDynamicCollectionId: id })),
      ...dynamicCollections
        .filter(({ source }) => source === 'SAVED_FILTER')
        .map(({ id }) => ({ deviceDynamicCollectionId: id })),
    ],
    [data?.network?.allDeviceGroups, dynamicCollections],
  );

  const optionData = useMemo(
    () => ({
      groups: indexOptionDataById(data?.network?.allDeviceGroups),
      dynamicCollections: indexDynamicCollectionDataById(dynamicCollections),
    }),
    [data?.network?.allDeviceGroups, dynamicCollections],
  );

  const formatDeviceCount = useCallback(
    (count: number) =>
      t('pages.device_signals.settings.target_groups.device_count_plural', { count }),
    [t],
  );

  const getOptionLabel = useCallback(
    (option: DeviceSignalTarget): string => {
      if (option.deviceDynamicCollectionId != null) {
        const data = optionData.dynamicCollections[option.deviceDynamicCollectionId];
        return data ? `${data.name} (${formatDeviceCount(data.deviceCount)})` : '';
      }
      if (option.deviceGroupId != null) {
        const data = optionData.groups[option.deviceGroupId];
        return data ? `${data.name} (${formatDeviceCount(data.deviceCount)})` : '';
      }
      return 'All Devices';
    },
    [optionData.dynamicCollections, optionData.groups, formatDeviceCount],
  );

  const groupBy = useCallback(
    (option: DeviceSignalTarget): string => {
      if (option.deviceGroupId != null) {
        return t('pages.device_signals.settings.target_groups.device_groups');
      }
      if (option.deviceDynamicCollectionId != null) {
        const data = optionData.dynamicCollections[option.deviceDynamicCollectionId];
        if (data?.source === 'MANUAL') {
          return t('pages.device_signals.settings.target_groups.device_collections');
        }
        if (data?.source === 'SAVED_FILTER') {
          return t('pages.device_signals.settings.target_groups.device_filters');
        }
      }
      return '';
    },
    [t, optionData.dynamicCollections],
  );

  const handleCollectionCreate = useCallback(
    async (collectionId: number) => {
      await refetch();
      onChange?.({ deviceDynamicCollectionId: collectionId });
    },
    [onChange, refetch],
  );

  const isOptionEqualToValue = useCallback(
    (option: DeviceSignalTarget, val: DeviceSignalTarget) => deepEqual(option, val),
    [],
  );

  // Custom Paper component with "Create New" button at the top
  const DropdownPaper = useCallback(
    ({ children, ...props }: PaperProps) => (
      <Paper {...props}>
        <Button
          fullWidth
          startIcon={<Add />}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setDialogOpen(true)}
          sx={{
            justifyContent: 'flex-start',
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            borderRadius: 0,
          }}
        >
          {t('pages.device_signals.settings.target_groups.create_new')}
        </Button>
        {children}
      </Paper>
    ),
    [t],
  );

  if (loading && !data?.network?.allDeviceGroups) return null;

  return (
    <>
      <Autocomplete
        id={ids?.inputId}
        fullWidth
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        groupBy={groupBy}
        loading={loading}
        options={options}
        onChange={(_, value) => onChange?.(value)}
        value={value}
        PaperComponent={DropdownPaper}
        renderGroup={(params) => (
          <li key={params.key}>
            {params.group && <AutocompleteGroupHeader>{params.group}</AutocompleteGroupHeader>}
            <AutocompleteGroupItems>{params.children}</AutocompleteGroupItems>
          </li>
        )}
        renderOption={(props, option) => (
          <li {...props} key={getOptionLabel(option)}>
            {getOptionLabel(option)}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            error={error}
            helperText={helperText}
            name={name}
            {...namedByPlaceholder(
              params,
              t('pages.device_signals.settings.placeholders.select_target'),
              ids != null,
            )}
          />
        )}
        disabled={disabled}
        onBlur={onBlur}
      />
      <NewDeviceCollectionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreate={handleCollectionCreate}
      />
    </>
  );
};
